import React, { useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../_components";
import { encrypt, history } from "../../_helpers";
import { alertActions } from "../../_actions";
import { alertConstants } from "../../_constants";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import classes from "../assets/css/vessels.module.css";
import { vesselServices } from "../services/vessels.services";

const validationSchema = Yup.object({
  sheetName: Yup.string()
    .max(40, "Sheet name must be at most 80 characters")
    .required("Sheet name is required"),
  vesselName: Yup.string()
    .max(30, "Vessel name must be at most 30 characters")
    .required("Vessel name is required"),
  vesselIMO: Yup.string()
    .max(20, "IMO number must be at most 20 characters")
    .required("IMO number is required"),
  vesselRegistryPort: Yup.string()
    .max(30, "Port of registry must be at most 30 characters")
    .required("Port of registry is required"),
  vesselFlag: Yup.string()
    .max(20, "Flag must be at most 20 characters")
    .required("Flag is required"),
  vesselType: Yup.string()
    .oneOf(["M/Y", "S/Y"], "Invalid type")
    .required("Type is required"),
  vesselLOA: Yup.number()
    .required("Length overall is required")
    .positive("Length overall must be positive"),
  vesselMaxPassengersWithStaterooms: Yup.number()
    .required("Maximum passengers with staterooms is required")
    .positive("Value must be positive"),
  vesselDayPassengersOnBoard: Yup.number()
    .required("Day passengers on board is required")
    .positive("Value must be positive"),
  vesselServiceProvider: Yup.string()
    .max(60, "Service provider must be at most 60 characters")
    .required("Service provider is required"),
  vesselServiceProviderAddress: Yup.string()
    .max(150, "Service provider address must be at most 150 characters")
    .required("Service provider address is required"),
  vesselServiceProviderCompany: Yup.string()
    .max(40, "Service provider Company ID must be at most 40 characters")
    .required("Service provider Company ID is required"),
  vesselServiceProviderInsuranceRef: Yup.string()
    .max(
      30,
      "Service provider insurance reference must be at most 30 characters"
    )
    .required("Service provider insurance reference is required"),
  vesselServiceProviderVAT: Yup.string()
    .max(20, "Service provider VAT number must be at most 20 characters")
    .required("Service provider VAT number is required"),
});

export default function AddVessel() {
  const { t } = useTranslation();
  const user = useSelector((state) => state.authentication.user);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [saveMode, setSaveMode] = useState("DEFAULT");
  const initialValues = useMemo(
    () => ({
      sheetName: "",
      vesselName: "",
      vesselIMO: "",
      vesselRegistryPort: "",
      vesselFlag: "",
      vesselType: "",
      vesselLOA: "",
      vesselMaxPassengersWithStaterooms: "",
      vesselDayPassengersOnBoard: "",
      vesselServiceProvider: "",
      vesselServiceProviderAddress: "",
      vesselServiceProviderCompany: "",
      vesselServiceProviderInsuranceRef: "",
      vesselServiceProviderVAT: "",
      vesselCentralAgent: user?.company?.name || "",
      vesselCentralAgentAddress: user?.company?.address1 || "",
      vesselCentralAgentCompany: user?.company?.registrationNumber || "",
      vesselCentralAgentInsuranceRef: user?.company?.insurance || "",
      vesselCentralAgentVAT: user?.company?.VATnumber || "",
      vesselCentralAgentMYBA: user?.company?.MYBAid || "",
      vesselStakeholder: "",
      vesselStakeholderAddress: "",
      vesselStakeholderCompany: "",
      vesselStakeholderInsuranceRef: "",
      vesselStakeholderMYBA: "",
      vesselStakeholderVAT: "",
      vesselStakeholderFinancialGuarantee: "",
      vesselSpecialConditions: "",
    }),
    [user]
  );
  const privateKey = useSelector((state) => state.cryptKeys.privateKey);

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      /* const payload = {
        ...values,
        vesselCentralAgentCompany: `${values?.vesselCentralAgentCompany}`,
        vesselStakeholderCompany: `${values?.vesselStakeholderCompany}`,
        vesselServiceProviderCompany: `${values?.vesselServiceProviderCompany}`,
      }; */
      const publicKey = user?.publicKey;
      const payload = {
        sheetName: encrypt(values?.sheetName, publicKey, privateKey),
        vesselName: encrypt(values?.vesselName, publicKey, privateKey),
        vesselIMO: encrypt(values?.vesselIMO, publicKey, privateKey),
        vesselRegistryPort: encrypt(
          values?.vesselRegistryPort,
          publicKey,
          privateKey
        ),
        vesselFlag: encrypt(values?.vesselFlag, publicKey, privateKey),
        vesselType: encrypt(values?.vesselType, publicKey, privateKey),
        vesselLOA: encrypt(`${values?.vesselLOA}`, publicKey, privateKey),
        vesselMaxPassengersWithStaterooms: encrypt(
          `${values?.vesselMaxPassengersWithStaterooms}`,
          publicKey,
          privateKey
        ),
        vesselDayPassengersOnBoard: encrypt(
          `${values?.vesselDayPassengersOnBoard}`,
          publicKey,
          privateKey
        ),
        vesselServiceProvider: encrypt(
          values?.vesselServiceProvider,
          publicKey,
          privateKey
        ),
        vesselServiceProviderAddress: encrypt(
          values?.vesselServiceProviderAddress,
          publicKey,
          privateKey
        ),
        vesselServiceProviderCompany: encrypt(
          `${values?.vesselServiceProviderCompany}`,
          publicKey,
          privateKey
        ),
        vesselServiceProviderInsuranceRef: encrypt(
          values?.vesselServiceProviderInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselServiceProviderVAT: encrypt(
          values?.vesselServiceProviderVAT,
          publicKey,
          privateKey
        ),
        vesselCentralAgent: encrypt(
          values?.vesselCentralAgent,
          publicKey,
          privateKey
        ),
        vesselCentralAgentAddress: encrypt(
          values?.vesselCentralAgentAddress,
          publicKey,
          privateKey
        ),
        vesselCentralAgentCompany: encrypt(
          `${values?.vesselCentralAgentCompany}`,
          publicKey,
          privateKey
        ),
        vesselCentralAgentInsuranceRef: encrypt(
          values?.vesselCentralAgentInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselCentralAgentVAT: encrypt(
          values?.vesselCentralAgentVAT,
          publicKey,
          privateKey
        ),
        vesselCentralAgentMYBA: encrypt(
          values?.vesselCentralAgentMYBA,
          publicKey,
          privateKey
        ),
        vesselStakeholder: encrypt(
          values?.vesselStakeholder,
          publicKey,
          privateKey
        ),
        vesselStakeholderAddress: encrypt(
          values?.vesselStakeholderAddress,
          publicKey,
          privateKey
        ),
        vesselStakeholderCompany: encrypt(
          `${values?.vesselStakeholderCompany}`,
          publicKey,
          privateKey
        ),
        vesselStakeholderInsuranceRef: encrypt(
          values?.vesselStakeholderInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselStakeholderMYBA: encrypt(
          values?.vesselStakeholderMYBA,
          publicKey,
          privateKey
        ),
        vesselStakeholderVAT: encrypt(
          values?.vesselStakeholderVAT,
          publicKey,
          privateKey
        ),
        vesselStakeholderFinancialGuarantee: encrypt(
          values?.vesselStakeholderFinancialGuarantee,
          publicKey,
          privateKey
        ),
        vesselSpecialConditions: encrypt(
          values?.vesselSpecialConditions,
          publicKey,
          privateKey
        ),
      };
      await vesselServices.add(payload, user?.id);
      history.push("/vessels");
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      dispatch(alertActions.setAlert(t(error), alertConstants?.ERROR));
      setIsLoading(false);
    }
  };

  const handleSubmitAndDefineUsers = async (values) => {
    try {
      setIsLoading(true);
      const publicKey = user?.publicKey;
      const payload = {
        sheetName: encrypt(values?.sheetName, publicKey, privateKey),
        vesselName: encrypt(values?.vesselName, publicKey, privateKey),
        vesselIMO: encrypt(values?.vesselIMO, publicKey, privateKey),
        vesselRegistryPort: encrypt(
          values?.vesselRegistryPort,
          publicKey,
          privateKey
        ),
        vesselFlag: encrypt(values?.vesselFlag, publicKey, privateKey),
        vesselType: encrypt(values?.vesselType, publicKey, privateKey),
        vesselLOA: encrypt(`${values?.vesselLOA}`, publicKey, privateKey),
        vesselMaxPassengersWithStaterooms: encrypt(
          `${values?.vesselMaxPassengersWithStaterooms}`,
          publicKey,
          privateKey
        ),
        vesselDayPassengersOnBoard: encrypt(
          `${values?.vesselDayPassengersOnBoard}`,
          publicKey,
          privateKey
        ),
        vesselServiceProvider: encrypt(
          values?.vesselServiceProvider,
          publicKey,
          privateKey
        ),
        vesselServiceProviderAddress: encrypt(
          values?.vesselServiceProviderAddress,
          publicKey,
          privateKey
        ),
        vesselServiceProviderCompany: encrypt(
          `${values?.vesselServiceProviderCompany}`,
          publicKey,
          privateKey
        ),
        vesselServiceProviderInsuranceRef: encrypt(
          values?.vesselServiceProviderInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselServiceProviderVAT: encrypt(
          values?.vesselServiceProviderVAT,
          publicKey,
          privateKey
        ),
        vesselCentralAgent: encrypt(
          values?.vesselCentralAgent,
          publicKey,
          privateKey
        ),
        vesselCentralAgentAddress: encrypt(
          values?.vesselCentralAgentAddress,
          publicKey,
          privateKey
        ),
        vesselCentralAgentCompany: encrypt(
          `${values?.vesselCentralAgentCompany}`,
          publicKey,
          privateKey
        ),
        vesselCentralAgentInsuranceRef: encrypt(
          values?.vesselCentralAgentInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselCentralAgentVAT: encrypt(
          values?.vesselCentralAgentVAT,
          publicKey,
          privateKey
        ),
        vesselCentralAgentMYBA: encrypt(
          values?.vesselCentralAgentMYBA,
          publicKey,
          privateKey
        ),
        vesselStakeholder: encrypt(
          values?.vesselStakeholder,
          publicKey,
          privateKey
        ),
        vesselStakeholderAddress: encrypt(
          values?.vesselStakeholderAddress,
          publicKey,
          privateKey
        ),
        vesselStakeholderCompany: encrypt(
          `${values?.vesselStakeholderCompany}`,
          publicKey,
          privateKey
        ),
        vesselStakeholderInsuranceRef: encrypt(
          values?.vesselStakeholderInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselStakeholderMYBA: encrypt(
          values?.vesselStakeholderMYBA,
          publicKey,
          privateKey
        ),
        vesselStakeholderVAT: encrypt(
          values?.vesselStakeholderVAT,
          publicKey,
          privateKey
        ),
        vesselStakeholderFinancialGuarantee: encrypt(
          values?.vesselStakeholderFinancialGuarantee,
          publicKey,
          privateKey
        ),
        vesselSpecialConditions: encrypt(
          values?.vesselSpecialConditions,
          publicKey,
          privateKey
        ),
      };

      const res = await vesselServices.add(payload, user?.id);
      window.scrollTo({ top: 0, behavior: "smooth" });
      history.push(`/vessels/edit/${res?.id}/associate`);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };
  const handleAsCentralAgent = (setFieldValue, values) => {
    setFieldValue("vesselStakeholder", values.vesselCentralAgent);
    setFieldValue("vesselStakeholderAddress", values.vesselCentralAgentAddress);
    setFieldValue("vesselStakeholderCompany", values.vesselCentralAgentCompany);
    setFieldValue(
      "vesselStakeholderInsuranceRef",
      values.vesselCentralAgentInsuranceRef
    );
    setFieldValue("vesselStakeholderVAT", values.vesselCentralAgentVAT);
    setFieldValue("vesselStakeholderMYBA", values.vesselCentralAgentMYBA);
  };
  return (
    <>
      <Loader isShown={isLoading} />
      <h1 className="page-title">{t("Add a new vessel")}</h1>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={
          saveMode === "SAVE_AND_DEFINE"
            ? handleSubmitAndDefineUsers
            : handleSubmit
        }
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <FormikForm>
            <Form.Group>
              <Form.Label>
                {t("Name")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                name="sheetName"
                placeholder={t("Sheet name")}
                maxLength={40}
                className="form-control"
              />
              <ErrorMessage
                name="sheetName"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Name of the vessel")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                name="vesselName"
                placeholder={t("Name of the vessel")}
                maxLength={30}
                className="form-control"
              />
              <ErrorMessage
                name="vesselName"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("IMO number")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("IMO number")}
                name="vesselIMO"
                maxLength={20}
                className="form-control"
              />
              <ErrorMessage
                name="vesselIMO"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Port of registry")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Port of registry")}
                name="vesselRegistryPort"
                maxLength={30}
                className="form-control"
              />
              <ErrorMessage
                name="vesselRegistryPort"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Flag")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Flag")}
                name="vesselFlag"
                maxLength={20}
                className="form-control"
              />
              <ErrorMessage
                name="vesselFlag"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Type")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Form.Group>
                <Field
                  type="radio"
                  label="M/Y"
                  value={"M/Y"}
                  name="vesselType"
                />
                <Form.Label className="ml-1">{t("M/Y")}</Form.Label>
                <Field
                  type="radio"
                  label={"S/Y"}
                  value={"S/Y"}
                  name="vesselType"
                  className="ml-1"
                />
                <Form.Label className="ml-1">{t("S/Y")}</Form.Label>{" "}
                <ErrorMessage
                  name="vesselType"
                  component="div"
                  className="text-danger"
                />
              </Form.Group>
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Length overall")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="number"
                placeholder={t("xxx.xx")}
                name="vesselLOA"
                className="form-control"
              />
              <ErrorMessage
                name="vesselLOA"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Maximum number of Passengers with staterooms")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="number"
                placeholder={t("xx")}
                name="vesselMaxPassengersWithStaterooms"
                className="form-control"
              />
              <ErrorMessage
                name="vesselMaxPassengersWithStaterooms"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Day Passengers on board")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="number"
                placeholder={t("xx")}
                name="vesselDayPassengersOnBoard"
                className="form-control"
              />
              <ErrorMessage
                name="vesselDayPassengersOnBoard"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <hr />
            <h1 className="page-subtitle">{t("Service Provider")}</h1>
            <Form.Group>
              <Form.Label>
                {t("Service provider")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider")}
                name="vesselServiceProvider"
                className="form-control"
                maxLength={40}
              />
              <ErrorMessage
                name="vesselServiceProvider"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Service provider address")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider address")}
                name="vesselServiceProviderAddress"
                className="form-control"
                maxLength={150}
              />
              <ErrorMessage
                name="vesselServiceProviderAddress"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Service provider Company ID")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider CompanyID")}
                name="vesselServiceProviderCompany"
                maxLength={40}
                className="form-control"
              />
              <ErrorMessage
                name="vesselServiceProviderCompany"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Service provider insurance reference")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider insurance reference")}
                name="vesselServiceProviderInsuranceRef"
                maxLength={30}
                className="form-control"
              />
              <ErrorMessage
                name="vesselServiceProviderInsuranceRef"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Service provider VAT number")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider VAT number")}
                name="vesselServiceProviderVAT"
                maxLength={20}
                className="form-control"
              />
              <ErrorMessage
                name="vesselServiceProviderVAT"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <hr />
            <h1 className="page-subtitle">{t("Central Agent")}</h1>
            <Form.Group>
              <Form.Label>{t("Central agent")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent")}
                name="vesselCentralAgent"
                className="form-control"
                maxLength={40}
              />
              <ErrorMessage
                name="vesselCentralAgent"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent address")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent address")}
                name="vesselCentralAgentAddress"
                maxLength={75}
                className="form-control"
              />
              <ErrorMessage
                name="vesselCentralAgentAddress"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent Company ID")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent Company ID")}
                name="vesselCentralAgentCompany"
                maxLength={40}
                className="form-control"
              />
              <ErrorMessage
                name="vesselCentralAgentCompany"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent insurance reference")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent insurance reference")}
                name="vesselCentralAgentInsuranceRef"
                maxLength={30}
                className="form-control"
              />
              <ErrorMessage
                name="vesselCentralAgentInsuranceRef"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent VAT number")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent VAT number")}
                name="vesselCentralAgentVAT"
                maxLength={20}
                className="form-control"
              />
              <ErrorMessage
                name="vesselCentralAgentVAT"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent MYBA id")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent MYBA id")}
                name="vesselCentralAgentMYBA"
                className="form-control"
                maxLength={10}
              />
              <ErrorMessage
                name="vesselCentralAgentMYBA"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <hr />
            <div className="d-flex align-items-center">
              <h1 className="page-subtitle">{t("Stakeholder")}</h1>{" "}
              <Button
                variant="outline-primary"
                style={{ marginLeft: "10px" }}
                onClick={() => handleAsCentralAgent(setFieldValue, values)}
              >
                {t("Same as Central Agent")}
              </Button>
            </div>
            <Form.Group>
              <Form.Label>{t("Stakeholder")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder")}
                name="vesselStakeholder"
                maxLength={40}
                className="form-control"
              />
              <ErrorMessage
                name="vesselStakeholder"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder address")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder address")}
                name="vesselStakeholderAddress"
                maxLength={75}
                className="form-control"
              />
              <ErrorMessage
                name="vesselStakeholderAddress"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder Company ID")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder Company ID")}
                name="vesselStakeholderCompany"
                maxLength={40}
                className="form-control"
              />
              <ErrorMessage
                name="vesselStakeholderCompany"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder insurance reference")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder insurance reference")}
                name="vesselStakeholderInsuranceRef"
                maxLength={30}
                className="form-control"
              />
              <ErrorMessage
                name="vesselStakeholderInsuranceRef"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder VAT number")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder VAT number")}
                name="vesselStakeholderVAT"
                maxLength={20}
                className="form-control"
              />
              <ErrorMessage
                name="vesselStakeholderVAT"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder MYBA id")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder MYBA id")}
                name="vesselStakeholderMYBA"
                maxLength={10}
                className="form-control"
              />
              <ErrorMessage
                name="vesselStakeholderMYBA"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder financial guarantee")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder financial guarantee")}
                name="vesselStakeholderFinancialGuarantee"
                maxLength={30}
                className="form-control"
              />
              <ErrorMessage
                name="vesselStakeholderFinancialGuarantee"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Special conditions")}</Form.Label>
              <Field
                as="textarea"
                type="text"
                name="vesselSpecialConditions"
                rows={50}
                className="form-control"
              />
            </Form.Group>
            <div className={classes.formButtonGroup}>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                onClick={() => setSaveMode("DEFAULT")}
              >
                {t("Save")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                onClick={() => setSaveMode("SAVE_AND_DEFINE")}
                disabled={isSubmitting}
              >
                {t("Save and define users")}
              </Button>
              <Button
                variant="primary"
                onClick={() => history.push("/vessels")}
              >
                {t("Cancel")}
              </Button>
            </div>
          </FormikForm>
        )}
      </Formik>
    </>
  );
}
